import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"
import { Notyf } from 'notyf'
import 'notyf/notyf.min.css'
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

Dropzone.autoDiscover = false

export default class extends Controller {
  static targets = [
    "input", "previews", "trigger", 
    "cropperWrapper", "cropperImage",
    "instructionText"
  ]
  
  static values = {
    ajax: { type: Boolean, default: false }, 
    maxFiles: { type: Number, default: 1 },
    modal: { type: Boolean, default: false }
  }

  connect() {
    this.notyf = new Notyf({ duration: 3000, ripple: true, position: { x: "right", y: "top" } })
    this.hasWarnedLimit = false 
    this._setupConfirmButton()
    this._initializeDropzone()
  }

  disconnect() {
    if (this.dropzone) this.dropzone.destroy()
    if (this.limitTimeout) clearTimeout(this.limitTimeout)
    this._cleanupCropper()
    
    if (this.isModalMode && this.confirmBtn) {
      this.confirmBtn.removeEventListener("click", this.boundConfirmSelection)
    }
  }

  // --- FORM SUBMISSION ---
  updateRules(event)
  {
    const acceptType = event.detail.acceptType
    this._updateDropzoneInternalTypes(acceptType)
    this._updateDropzoneAreaText(acceptType)
    
  }
  _updateDropzoneInternalTypes(acceptType) {
    if (!this.dropzone) return;

    // 1. If it's a wildcard or empty, reset Dropzone to allow your default formats
    if (!acceptType || acceptType === "*/*" || acceptType.trim() === "") {
      this.dropzone.options.acceptedFiles = "image/jpeg,image/png,image/webp,video/mp4";
      if (this.dropzone.hiddenFileInput) {
        this.dropzone.hiddenFileInput.removeAttribute("accept");
      }
      return;
    }

    // 2. Otherwise, enforce the specific type requested by the button
    this.dropzone.options.acceptedFiles = acceptType;
    
    // Use Dropzone's built-in hiddenFileInput, NOT Stimulus's inputTarget
    if (this.dropzone.hiddenFileInput) {
      this.dropzone.hiddenFileInput.setAttribute("accept", acceptType);
    }
  }
  _updateDropzoneAreaText(acceptType){
    if(this.hasInstructionTextTarget && acceptType){
      if (acceptType.includes('video')) {
        this.instructionTextTarget.innerText = "Drop video file here or click to upload";
      } else if (acceptType.includes('image')) {
        this.instructionTextTarget.innerText = "Drop image here or click to upload";
      } else {
        this.instructionTextTarget.innerText = "Drop files here or click to upload";
      }
    }
  }
  

  handleSubmit(event) {
    if (!this.ajaxValue) return 
    
    const queuedFiles = this.dropzone.getQueuedFiles()

    if (queuedFiles.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      
      // Tell Dropzone to upload the files via AJAX
      this.dropzone.processQueue()
    } else {
      this.notyf.error("Please add and crop an image first.")
      event.preventDefault()
    }
  }

  // --- COORDINATORS ---

  processAddedFile(file) {
    // Strict OS-Bypass Check because user can still choose (all files) and add invalid type
    if (!this._isValidFileType(file)) return;
    this.enforceSingleFileLimit()
    this._handleVideoThumbnail(file)//if file type is video
    if (this._shouldCrop(file)) {
      this.initializeCropper(file)
    } else if (this.isModalMode) {
      this.stageFileForConfirmation(file)
    }
  }
  
  // --- CROPPER ACTIONS ---

  initializeCropper(file) {
    this.originalCroppingFile = file 
    this.targetCropWidth ||= 800
    this.targetCropHeight ||= 800

    this._toggleCropperUI(true)

    const url = URL.createObjectURL(file)
    this.cropperImageTarget.src = url
    this.cropperImageTarget.classList.remove("opacity-0")

    this._cleanupCropper()
    this.cropper = new Cropper(this.cropperImageTarget, {
      viewMode: 2, 
      autoCropArea: 1, 
      background: false,
      aspectRatio: this.targetCropWidth / this.targetCropHeight, 
    })
  }

  setFormat(event) {
    this.targetCropWidth = parseInt(event.currentTarget.dataset.width)
    this.targetCropHeight = parseInt(event.currentTarget.dataset.height)

    if (this.cropper) {
      this.cropper.setAspectRatio(this.targetCropWidth / this.targetCropHeight)
    }
  }

  cancelCrop() {
    this._cleanupCropper()
    this._toggleCropperUI(false)
    this.dropzone.removeFile(this.originalCroppingFile)
    this.originalCroppingFile = null
  }

  applyCrop() {
    if (!this.cropper) return

    const canvasOptions = {
      width: this.targetCropWidth,
      height: this.targetCropHeight,
      fillColor: '#fff', 
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    }

    this.cropper.getCroppedCanvas(canvasOptions).toBlob((blob) => {
      const name = this.originalCroppingFile.name.replace(/\.[^/.]+$/, ".jpg")
      const croppedFile = new File([blob], name, { type: 'image/jpeg' })
      
      croppedFile.isCropped = true 
      croppedFile.status = Dropzone.QUEUED // IMPORTANT: Mark as ready for upload
      
      this._cleanupCropper()
      this._toggleCropperUI(false)
      
      this.dropzone.removeFile(this.originalCroppingFile)
      this.dropzone.addFile(croppedFile) 

      // Force thumbnail generation so you can see the cropped result
      this.dropzone.createThumbnail(
        croppedFile, 
        this.dropzone.options.thumbnailWidth, 
        this.dropzone.options.thumbnailHeight, 
        "crop", 
        true, 
        (dataUrl) => {
          croppedFile.dataURL = dataUrl;
          this.dropzone.emit("thumbnail", croppedFile, dataUrl);
        }
      )
      
    }, 'image/jpeg', 0.92)
  }

  // --- DROPZONE WORKERS ---

  enforceSingleFileLimit() {
    if (this.isSingleFileMode && this.dropzone.files.length > 1) {
      this.dropzone.removeFile(this.dropzone.files[0])
    }
  }

  stageFileForConfirmation(file) {
    this.stagedFile = file
    this._toggleConfirmButton(true)
  }

  // --- PRIVATE INITIALIZERS ---

  _initializeDropzone() {
    const form = this.element.closest("form")
    const inputName = this.inputTarget ? this.inputTarget.getAttribute("name") : "file"

    this.dropzone = new Dropzone(this.element, {
      url: this.ajaxValue && form ? form.action : "#",
      paramName: inputName,
      acceptedFiles: "image/jpeg,image/png,image/webp,video/mp4",    
      autoProcessQueue: false,
      addRemoveLinks: true,
      parallelUploads: this.ajaxValue ? 15 : 1,      
      maxFiles: this.maxFilesValue, 
      maxFilesize: 20,  
      thumbnailWidth: 300,           
      thumbnailHeight: 300,
      dictRemoveFile: `
        <div class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-600 bg-red-50 border border-red-100 rounded-md hover:bg-red-600 hover:text-white transition-all group">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span>Delete</span>
        </div>
        `,
      clickable: this.hasTriggerTarget ? this.triggerTarget : true,
      previewsContainer: this.hasPreviewsTarget ? this.previewsTarget : null,
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
        "Accept": "application/json" 
      }
    })

    this._bindDropzoneEvents()
  }

  _bindDropzoneEvents() {
    this.dropzone.on("addedfile", (file) => this.processAddedFile(file))
    
    this.dropzone.on("removedfile", (file) => {
      if (this.stagedFile === file) this.stagedFile = null
      if (this.dropzone.files.length === 0) this._toggleConfirmButton(false)
    })

    this.dropzone.on("sending", (file, xhr, formData) => {
      const form = this.element.closest("form")
      new FormData(form).forEach((value, key) => {
        if (key !== this.inputTarget.getAttribute("name")) {
          formData.append(key, value)
        }
      })
    })

    // IMPORTANT: Redirects after bulk upload success
    this.dropzone.on("queuecomplete", () => {
      if (this.ajaxValue && !this.modalValue) {
        window.location.href = "/admin/assets"
      }
    })

    this.dropzone.on("maxfilesexceeded", (file) => {
      this.dropzone.removeFile(file)
      if (!this.hasWarnedLimit) {
        this.notyf.error(`Limit reached: Only ${this.maxFilesValue} file(s) allowed.`)
        this.hasWarnedLimit = true
        this.limitTimeout = setTimeout(() => { this.hasWarnedLimit = false }, 2000)
      }
    })
  }

  _setupConfirmButton() {
    this.confirmBtn = document.getElementById("confirm-media-selection") 
    this.boundConfirmSelection = (e) => {
      e.preventDefault()
      if (window.activeMediaPicker && this.stagedFile) {
        window.activeMediaPicker.selectRawFile(this.stagedFile)
        this.dropzone.removeFile(this.stagedFile)
        this.stagedFile = null
        this._toggleConfirmButton(false)
      }
    }

    if (this.isModalMode && this.confirmBtn) {
      this.confirmBtn.addEventListener("click", this.boundConfirmSelection)
    }
  }

  // --- HELPERS ---
 async _handleVideoThumbnail(file) {
  if (!file.type.startsWith("video/")) return

  const video = this._createHiddenVideoElement(file)

  try {
    await this._seekToThumbnailFrame(video)
    const dataUrl = this._extractFrameToDataUrl(video)
    file.dataURL = dataUrl
    this.dropzone.emit("thumbnail", file, dataUrl)
  } catch (error) {
    console.warn(`Could not generate thumbnail for ${file.name}: ${error.message}`)
  } finally {
    this._cleanupVideoElement(video) // Guaranteed to run, even on failure
  }
}

_isValidFileType(file) {
    const allowedTypes = this.dropzone.options.acceptedFiles;
    
    if (!allowedTypes || allowedTypes === "*/*" || allowedTypes === "image/jpeg,image/png,image/webp,video/mp4") {
      // make sure it is either image or video, since our default accepts both
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        this.dropzone.removeFile(file);
        this.notyf.error("Invalid file format. Please upload an image or video.");
        return false;
      }
      return true; // Default case allows common image/video formats
    }

    //  The rule has only video types
    if (allowedTypes.includes("video") && !allowedTypes.includes("image")) {
      if (!file.type.startsWith("video/")) {
        this.dropzone.removeFile(file);
        this.notyf.error("Invalid file. Please select a valid Video (mp4).");
        return false;
      }
    }
    
    //  The rule has only image types
    if (allowedTypes.includes("image") && !allowedTypes.includes("video")) {
      if (!file.type.startsWith("image/")) {
        this.dropzone.removeFile(file);
        this.notyf.error("Invalid file. Please select a valid Image.");
        return false;
      }
    }

    // It passed the checks!
    return true;
  }

_createHiddenVideoElement(file) {
  const video = document.createElement("video")
  Object.assign(video, {
    style: "display: none;",
    muted: true,
    playsInline: true,
    preload: "metadata",
    src: URL.createObjectURL(file)
  })
  return video
}

_seekToThumbnailFrame(video) {
  return new Promise((resolve, reject) => {
    video.onerror = () => reject(new Error("Browser cannot decode format"))

    video.addEventListener("loadedmetadata", () => {
      video.currentTime = Math.min(1, video.duration / 2)
    }, { once: true })

    video.addEventListener("seeked", resolve, { once: true })
  })
}

_extractFrameToDataUrl(video) {
  const maxDimension = 600
  const scaleRatio = Math.min(maxDimension / video.videoWidth, maxDimension / video.videoHeight, 1)
  
  const canvas = document.createElement("canvas")
  canvas.width = video.videoWidth * scaleRatio
  canvas.height = video.videoHeight * scaleRatio
  
  const ctx = canvas.getContext("2d")
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
  
  return canvas.toDataURL("image/jpeg", 0.7)
}

_cleanupVideoElement(video) {
  if (video.src) URL.revokeObjectURL(video.src)
  video.remove()
}

  _shouldCrop(file) {
    return file.type.startsWith("image/") && !file.isCropped
  }

  _cleanupCropper() {
    if (this.cropper) this.cropper.destroy()
  }

  _toggleCropperUI(show) {
    this.cropperWrapperTarget.classList.toggle("hidden", !show)
    if (this.hasTriggerTarget) this.triggerTarget.classList.toggle("hidden", show)
  }

  _toggleConfirmButton(show) {
    if (this.confirmBtn) this.confirmBtn.classList.toggle("hidden", !show)
  }

  get isModalMode() { return this.modalValue }
  get isSingleFileMode() { return this.maxFilesValue === 1 }
}